import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Chip, Switch, FormControlLabel, Divider, CircularProgress,
} from '@mui/material';
import {
  Add, Edit, Delete, ArrowBack, Save, Code,
  PlayArrow, CheckCircle, Cancel, Info,
} from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { fmt, sym } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const TX_TYPES    = ['Income', 'Deduction'];
const SPECIAL_TYPES = ['None','PAYE','NSSF','SHIF','HousingLevy','Pension','PRMF','InsurancePremium','NonCashBenefit','FringeBenefit'];
const GROUPINGS   = ['Basic','Allowance','Statutory','OtherDeduction','Employer'];

const SPECIAL_COLORS = {
  PAYE: '#c62828', NSSF: '#1565c0', SHIF: '#2e7d32',
  HousingLevy: '#6a1b9a', Pension: '#e65100', PRMF: '#00695c',
  InsurancePremium: '#0277bd', NonCashBenefit: '#5d4037', FringeBenefit: '#4e342e',
  None: '#546e7a',
};

const emptyForm = {
  code: '', description: '', transactionType: 'Income', specialType: 'None',
  sequence: 50, transactionGrouping: 'Allowance',
  isFormulaBased: false, formula: '', employerFormula: '',
  defaultAmount: 0,
  isActive: true, isTaxable: false, reduceTaxablePay: false,
  allowable: false, hasBalance: false, balanceType: 'None',
  isOneOff: false, dontProrate: false, isVoluntaryPension: false,
  hasEmployerContribution: false,
  hasLimits: false, lowerLimit: 0, upperLimit: 0,
  nssfTier: 'Combined',
};

const FORMULA_TOKENS = [
  { token: 'GROSS',          desc: 'Total earnings so far' },
  { token: 'TAXABLE_GROSS',  desc: 'Taxable earnings so far' },
  { token: 'TAXABLE_PAY',    desc: 'Gross minus pension relief (PAYE base)' },
  { token: 'BASIC',          desc: 'Basic salary amount' },
  { token: 'INSURANCE_PREM', desc: 'Insurance premium deducted' },
  { token: "rate('CODE')",   desc: "Lookup statutory rate by code, e.g. rate('SHIF_RATE')" },
];

const TransactionCodeList = () => {
  const navigate = useNavigate();
  const [codes,   setCodes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  const [dlgOpen,  setDlgOpen]  = useState(false);
  const [form,     setForm]     = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dlgOpen, value: form, onClose: () => setDlgOpen(false) });
  const [isEdit,   setIsEdit]   = useState(false);
  const [saving,   setSaving]   = useState(false);

  // Delete code confirmation
  const [deleteCodeDlg, setDeleteCodeDlg] = useState(null);

  // Formula test
  const [testGross,  setTestGross]  = useState('100000');
  const [testResult, setTestResult] = useState(null);
  const [testing,    setTesting]    = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/payroll/transaction-codes`, { headers: hdrs() });
      setCodes(res.data);
    } catch { setError('Failed to load transaction codes'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg, isErr = false) => {
    if (isErr) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 3500);
  };

  const openAdd = () => {
    const nextSeq = codes.length ? Math.max(...codes.map(c => c.sequence)) + 10 : 10;
    setForm({ ...emptyForm, sequence: nextSeq });
    setIsEdit(false);
    setTestResult(null);
    setDlgOpen(true);
  };

  const openEdit = (c) => {
    setForm({
      code: c.code, description: c.description,
      transactionType: c.transactionType, specialType: c.specialType,
      sequence: c.sequence, transactionGrouping: c.transactionGrouping,
      isFormulaBased: c.isFormulaBased, formula: c.formula || '',
      employerFormula: c.employerFormula || '',
      defaultAmount: c.defaultAmount || 0,
      isActive: c.isActive, isTaxable: c.isTaxable,
      reduceTaxablePay: c.reduceTaxablePay, allowable: c.allowable,
      hasBalance: c.hasBalance, balanceType: c.balanceType || 'None',
      isOneOff: c.isOneOff, dontProrate: c.dontProrate, isVoluntaryPension: !!c.isVoluntaryPension,
      hasEmployerContribution: c.hasEmployerContribution,
      hasLimits: c.hasLimits, lowerLimit: c.lowerLimit || 0, upperLimit: c.upperLimit || 0,
      nssfTier: c.nssfTier || 'Combined',
    });
    setIsEdit(true);
    setTestResult(null);
    setDlgOpen(true);
  };

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const save = async () => {
    if (!form.code.trim())        return setError('Code is required');
    if (!form.description.trim()) return setError('Description is required');
    setSaving(true); setError('');
    try {
      if (isEdit) {
        await axios.put(`${API}/api/payroll/transaction-codes/${form.code}`, form, { headers: hdrs() });
      } else {
        await axios.post(`${API}/api/payroll/transaction-codes`, { ...form, code: form.code.trim().toUpperCase() }, { headers: hdrs() });
      }
      flash(isEdit ? 'Saved' : 'Created');
      setDlgOpen(false);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try {
      await axios.put(`${API}/api/payroll/transaction-codes/${c.code}`,
        { ...c, isActive: !c.isActive }, { headers: hdrs() });
      load();
    } catch (err) { flash(err.response?.data?.error || 'Update failed', true); }
  };

  const confirmDeleteCode = async () => {
    if (!deleteCodeDlg) return;
    try {
      await axios.delete(`${API}/api/payroll/transaction-codes/${deleteCodeDlg.code}`, { headers: hdrs() });
      flash('Deleted'); setDeleteCodeDlg(null);
      load();
    } catch (err) { flash(err.response?.data?.error || 'Delete failed', true); }
  };

  const testFormula = async () => {
    if (!form.code || !form.formula) return;
    setTesting(true); setTestResult(null);
    try {
      const res = await axios.get(
        `${API}/api/payroll/transaction-codes/${form.code}/preview?gross=${testGross}`,
        { headers: hdrs() }
      );
      setTestResult(res.data);
    } catch (err) { setTestResult({ error: err.response?.data?.error || 'Preview failed' }); }
    finally { setTesting(false); }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, mb: 6 }}>

      {/* Header */}
      <Card elevation={3} sx={{ mb: 2, background: 'linear-gradient(135deg, #263238 0%, #37474f 100%)', color: '#fff' }}>
        <CardContent sx={{ py: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Button startIcon={<ArrowBack />} size="small" variant="outlined"
                onClick={() => navigate('/payroll/setup')}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' } }}>
                Back
              </Button>
              <Code />
              <Box>
                <Typography variant="h6" fontWeight={700}>Transaction Codes</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Define earnings and deductions — each with its own formula and sequence
                </Typography>
              </Box>
            </Box>
            <Button startIcon={<Add />} variant="contained" onClick={openAdd}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
              New Code
            </Button>
          </Box>
        </CardContent>
      </Card>

      {loading && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {!loading && (
        <Card elevation={2}>
          <CardContent sx={{ p: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#263238' }}>
                  {['Code','Description','Type','Special','Seq','Grouping','Formula / Amount','Active',''].map(h => (
                    <TableCell key={h} sx={{ color: '#fff', fontWeight: 700, py: 1.2 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {codes.map((c, i) => (
                  <TableRow key={c.code} hover sx={{ bgcolor: i % 2 === 0 ? 'inherit' : '#fafafa', opacity: c.isActive ? 1 : 0.5 }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} fontFamily="monospace">{c.code}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2">{c.description}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        label={c.transactionType}
                        size="small"
                        sx={{
                          height: 20, fontSize: 11,
                          bgcolor: c.transactionType === 'Income' ? '#e8f5e9' : '#fce4ec',
                          color:   c.transactionType === 'Income' ? '#2e7d32' : '#c62828',
                        }} />
                    </TableCell>
                    <TableCell>
                      {c.specialType !== 'None' && (
                        <Chip label={c.specialType} size="small"
                          sx={{ height: 20, fontSize: 10, bgcolor: `${SPECIAL_COLORS[c.specialType]}18`, color: SPECIAL_COLORS[c.specialType] }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} color="text.secondary">{c.sequence}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{c.transactionGrouping}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      {c.isFormulaBased && c.formula ? (
                        <Typography variant="caption" fontFamily="monospace" color="primary.main"
                          sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.formula}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {c.defaultAmount ? `${sym()} ${fmt(c.defaultAmount)}` : '—'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={c.isActive ? 'Active — click to deactivate' : 'Inactive — click to activate'}>
                        <IconButton size="small" onClick={() => toggleActive(c)}>
                          {c.isActive
                            ? <CheckCircle fontSize="small" sx={{ color: '#2e7d32' }} />
                            : <Cancel    fontSize="small" sx={{ color: '#bbb' }} />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(c)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteCodeDlg(c)}><Delete fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {codes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No transaction codes. Click "New Code" to add one (Kenya defaults will auto-seed on first payroll calculation).
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Delete Transaction Code Confirmation Dialog */}
      <Dialog open={Boolean(deleteCodeDlg)} onClose={() => setDeleteCodeDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Transaction Code</DialogTitle>
        <DialogContent>
          <Typography>
            Delete code <strong>{deleteCodeDlg?.code}</strong> — {deleteCodeDlg?.description}? This will fail if it has been used in payroll entries. Consider deactivating instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCodeDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteCode}>Delete Anyway</Button>
        </DialogActions>
      </Dialog>

      {/* ── Add / Edit Dialog ── */}
      {discardDialog}
      <Dialog open={dlgOpen} onClose={attemptClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#263238', color: '#fff', py: 1.5 }}>
          {isEdit ? `Edit — ${form.code}` : 'New Transaction Code'}
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          <Grid container spacing={2}>

            {/* Row 1: Code, Description, Type, Special */}
            <Grid item xs={12} sm={3}>
              <TextField label="Code *" size="small" fullWidth
                value={form.code}
                onChange={e => set('code', e.target.value.toUpperCase())}
                disabled={isEdit}
                inputProps={{ style: { fontFamily: 'monospace', fontWeight: 700 } }}
                helperText={isEdit ? 'Code cannot be changed' : 'Unique, e.g. HOUSE'} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField label="Description *" size="small" fullWidth
                value={form.description} onChange={e => set('description', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField label="Type" size="small" fullWidth select
                value={form.transactionType} onChange={e => set('transactionType', e.target.value)}>
                {TX_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField label="Sequence" size="small" fullWidth type="number"
                value={form.sequence} onChange={e => set('sequence', Number(e.target.value))}
                helperText="Lower runs first" />
            </Grid>

            {/* Row 2: Special Type, Grouping, Default Amount */}
            <Grid item xs={12} sm={4}>
              <TextField label="Special Type" size="small" fullWidth select
                value={form.specialType} onChange={e => set('specialType', e.target.value)}>
                {SPECIAL_TYPES.map(t => (
                  <MenuItem key={t} value={t}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SPECIAL_COLORS[t] }} />
                      {t}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Grouping" size="small" fullWidth select
                value={form.transactionGrouping} onChange={e => set('transactionGrouping', e.target.value)}>
                {GROUPINGS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label={`Default Amount (${sym()})`} size="small" fullWidth type="number"
                value={form.defaultAmount} onChange={e => set('defaultAmount', Number(e.target.value))}
                helperText="Used when no employee line and no formula" />
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* Formula section */}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.isFormulaBased} onChange={e => set('isFormulaBased', e.target.checked)} />}
                label={<Typography variant="body2" fontWeight={600}>Formula-based calculation</Typography>}
              />
            </Grid>

            {form.isFormulaBased && (
              <>
                <Grid item xs={12}>
                  <TextField label="Employee Formula" size="small" fullWidth multiline rows={2}
                    value={form.formula} onChange={e => set('formula', e.target.value)}
                    inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
                    placeholder="e.g.  GROSS * rate('SHIF_RATE') / 100" />
                </Grid>

                {/* Token reference */}
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                      <Info fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
                      <Typography variant="caption" fontWeight={700} color="text.secondary">Available formula tokens</Typography>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {FORMULA_TOKENS.map(t => (
                        <Tooltip key={t.token} title={t.desc} arrow>
                          <Chip label={t.token} size="small"
                            sx={{ fontFamily: 'monospace', fontSize: 11, bgcolor: '#e3f2fd', cursor: 'help' }} />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                </Grid>

                {/* Test formula */}
                {isEdit && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                      <TextField label={`Test with gross (${sym()})`} size="small" type="number"
                        value={testGross} onChange={e => setTestGross(e.target.value)}
                        sx={{ width: 180 }} />
                      <Button variant="outlined" size="small" startIcon={<PlayArrow />}
                        onClick={testFormula} disabled={testing || !form.formula}>
                        {testing ? 'Testing…' : 'Test Formula'}
                      </Button>
                      {testResult && !testResult.error && (
                        <Typography variant="body2" fontWeight={700} color="success.main">
                          Result: {sym()} {fmt(testResult.result)}
                        </Typography>
                      )}
                      {testResult?.error && (
                        <Typography variant="body2" color="error">{testResult.error}</Typography>
                      )}
                    </Box>
                  </Grid>
                )}
              </>
            )}

            {/* Employer contribution */}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.hasEmployerContribution} onChange={e => set('hasEmployerContribution', e.target.checked)} />}
                label={<Typography variant="body2" fontWeight={600}>Has employer contribution</Typography>}
              />
            </Grid>

            {form.hasEmployerContribution && (
              <Grid item xs={12}>
                <TextField label="Employer Formula" size="small" fullWidth multiline rows={2}
                  value={form.employerFormula} onChange={e => set('employerFormula', e.target.value)}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
                  placeholder="e.g.  GROSS * rate('HOUSING_LEVY_ER') / 100" />
              </Grid>
            )}

            <Grid item xs={12}><Divider /></Grid>

            {/* Behaviour flags */}
            <Grid item xs={12}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>
                BEHAVIOUR FLAGS
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {[
                  { field: 'isTaxable',           label: 'Taxable (income)',      help: 'Counts toward taxable gross for PAYE' },
                  { field: 'reduceTaxablePay',    label: 'Reduces taxable pay',  help: 'Pension-type: reduces PAYE base by amount, up to maxPension cap' },
                  { field: 'allowable',           label: 'KRA allowable',        help: 'Registered pension: deductible against PAYE' },
                  { field: 'isVoluntaryPension',  label: 'Voluntary pension',    help: 'Voluntary pension: tracked separately from mandatory for P9 and excess pension tax' },
                  { field: 'isOneOff',            label: 'One-off',              help: 'Counts down one period per run until it reaches zero and deactivates' },
                  { field: 'dontProrate',         label: "Don't prorate",        help: 'Keep full amount even for mid-period joiners / leavers' },
                ].map(({ field, label, help }) => (
                  <Tooltip key={field} title={help} arrow>
                    <FormControlLabel
                      control={<Switch size="small" checked={!!form[field]} onChange={e => set(field, e.target.checked)} />}
                      label={<Typography variant="caption">{label}</Typography>}
                      sx={{ mr: 1 }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Grid>

            {/* Limits */}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.hasLimits} onChange={e => set('hasLimits', e.target.checked)} />}
                label={<Typography variant="body2" fontWeight={600}>Has limits (min / max amount)</Typography>}
              />
            </Grid>

            {form.hasLimits && (
              <>
                <Grid item xs={6} sm={3}>
                  <TextField label={`Lower limit (${sym()})`} size="small" fullWidth type="number"
                    value={form.lowerLimit} onChange={e => set('lowerLimit', Number(e.target.value))} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label={`Upper limit (${sym()})`} size="small" fullWidth type="number"
                    value={form.upperLimit} onChange={e => set('upperLimit', Number(e.target.value))}
                    helperText="0 = no cap" />
                </Grid>
              </>
            )}

            {/* NSSF tier — only visible for NSSF special type */}
            {form.specialType === 'NSSF' && (
              <Grid item xs={12} sm={4}>
                <TextField label="NSSF Tier" size="small" fullWidth select
                  value={form.nssfTier} onChange={e => set('nssfTier', e.target.value)}>
                  {['Combined','Tier1','Tier2','Voluntary'].map(t => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {/* Active */}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={e => set('isActive', e.target.checked)} />}
                label={<Typography variant="body2" fontWeight={600}>Active — included in payroll calculations</Typography>}
              />
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          {error && <Typography variant="caption" color="error" sx={{ flex: 1 }}>{error}</Typography>}
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving}
            sx={{ bgcolor: '#263238', '&:hover': { bgcolor: '#37474f' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default TransactionCodeList;
